// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Parcours « mot de passe oublié », en deux écrans publics :
//   /forgot-password        → demander un lien       (ForgotPassword)
//   /reset-password/:token  → poser le nouveau mdp   (ResetPassword)
//
// Les deux vivent dans le même fichier : ils partagent la mise en page, sont lus
// l'un après l'autre par l'utilisateur, et se comprennent mieux ensemble que
// séparés. Même parti pris que `LegalPage.js`, qui sert trois routes.
//
// ⚠️ L'écran de demande affiche TOUJOURS le même message de confirmation, que le
// compte existe ou non. Le serveur répond déjà de façon indifférenciée ; afficher
// « aucun compte pour cette adresse » ici rétablirait l'oracle que le serveur
// s'applique à ne pas donner.
import React, { useState } from 'react';
import { useRoute, useLocation } from 'wouter';
import { useI18n } from '../i18n';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.normascan.fr';

const Coquille = ({ titre, children }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
    <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow-lg">
      <div className="flex justify-center">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
          <span className="text-white text-3xl font-bold">N</span>
        </div>
      </div>
      <h2 className="text-center text-2xl font-bold text-gray-900">{titre}</h2>
      {children}
    </div>
  </div>
);

const LienRetour = () => {
  const { t } = useI18n();
  return (
    <div className="text-center text-sm">
      <a href="/login" className="text-blue-600 hover:underline">{t('pwd_back_to_login')}</a>
    </div>
  );
};

const champ = 'mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500';
const bouton = 'w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50';

export function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [envoye, setEnvoye] = useState(false);

  const soumettre = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch {
      // Volontairement silencieux : le message de confirmation ne doit dépendre
      // ni de l'existence du compte, ni de l'état du réseau. Un utilisateur qui
      // ne reçoit rien refait la demande.
    }
    setEnvoye(true);
    setEnvoi(false);
  };

  if (envoye) {
    return (
      <Coquille titre={t('pwd_forgot_sent_title')}>
        <p className="text-sm text-gray-700 leading-relaxed">{t('pwd_forgot_sent_body')}</p>
        <LienRetour />
      </Coquille>
    );
  }

  return (
    <Coquille titre={t('pwd_forgot_title')}>
      <p className="text-sm text-gray-600 leading-relaxed">{t('pwd_forgot_intro')}</p>
      <form className="space-y-4" onSubmit={soumettre}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            {t('pwd_forgot_email_label')}
          </label>
          <input
            id="email" type="email" autoComplete="email" required autoFocus
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.com" className={champ} disabled={envoi}
          />
        </div>
        <button type="submit" disabled={envoi} className={bouton}>
          {envoi ? t('pwd_forgot_sending') : t('pwd_forgot_submit')}
        </button>
      </form>
      <LienRetour />
    </Coquille>
  );
}

export function ResetPassword() {
  const { t } = useI18n();
  const [, params] = useRoute('/reset-password/:token');
  const [, setLocation] = useLocation();

  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [termine, setTermine] = useState(false);

  const soumettre = async (e) => {
    e.preventDefault();
    setErreur('');
    if (motDePasse !== confirmation) {
      setErreur(t('pwd_mismatch'));
      return;
    }
    setEnvoi(true);
    try {
      const reponse = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: params?.token, password: motDePasse }),
      });
      const donnees = await reponse.json();
      if (!reponse.ok) throw new Error(donnees.error || t('pwd_error_generic'));
      setTermine(true);
    } catch (err) {
      setErreur(err.message || t('pwd_error_generic'));
    } finally {
      setEnvoi(false);
    }
  };

  if (termine) {
    return (
      <Coquille titre={t('pwd_reset_done_title')}>
        <p className="text-sm text-gray-700 leading-relaxed">{t('pwd_reset_done_body')}</p>
        <button type="button" className={bouton} onClick={() => setLocation('/login')}>
          {t('pwd_back_to_login')}
        </button>
      </Coquille>
    );
  }

  return (
    <Coquille titre={t('pwd_reset_title')}>
      <form className="space-y-4" onSubmit={soumettre}>
        {erreur && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
            {erreur}
          </div>
        )}
        <div>
          <label htmlFor="motDePasse" className="block text-sm font-medium text-gray-700">
            {t('pwd_reset_password_label')}
          </label>
          <input
            id="motDePasse" type="password" autoComplete="new-password" required autoFocus
            value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)}
            placeholder="••••••••" className={champ} disabled={envoi}
          />
          <p className="mt-1 text-xs text-gray-500">{t('pwd_rules')}</p>
        </div>
        <div>
          <label htmlFor="confirmation" className="block text-sm font-medium text-gray-700">
            {t('pwd_reset_confirm_label')}
          </label>
          <input
            id="confirmation" type="password" autoComplete="new-password" required
            value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
            placeholder="••••••••" className={champ} disabled={envoi}
          />
        </div>
        <button type="submit" disabled={envoi} className={bouton}>
          {envoi ? t('pwd_reset_submitting') : t('pwd_reset_submit')}
        </button>
      </form>
      <LienRetour />
    </Coquille>
  );
}
